/**
 * Onboarding recipes — context-selected onboarding copy.
 *
 * The chat-window onboarding (see `useChatOnboarding.ts`) seeds two assistant
 * lines: a welcome, then after a short beat a one-line invite ("Look around, or
 * ask me anything.") — display-only, not gated on sync readiness (#5387) — and
 * then leaves the user to explore/ask freely. There are no suggestion chips.
 * Which copy runs is chosen by CONTEXT — a `?ob=<id>` URL param or the signed-in
 * email's domain — so e.g. a lawyer signing up from `@nulegal.com` (or anyone
 * visiting `?ob=lawyer`) gets a legal-focused welcome instead of the default.
 *
 * Recipes are pure data. Resolution (`resolveOnboardingRecipe`) is pure and
 * always returns a valid recipe (default fallback).
 */
export interface OnboardingRecipe {
  id: string
  welcome: string
  /** Short invite shown after the welcome — the user then explores/asks freely. */
  lookAround: string
}

// Onboarding is just an opening message: a welcome + a one-line invite to
// explore. No suggestion chips — the user drives from here (they asked to drop
// the "show you a few things" offer and the buttons). Recipes now differ only in
// the welcome wording; the invite is the same everywhere.
export const ONBOARDING_RECIPES: Record<string, OnboardingRecipe> = {
  default: {
    id: 'default',
    welcome: 'Welcome to XAVIOR.',
    lookAround: 'Look around, or ask me anything.',
  },
  lawyer: {
    id: 'lawyer',
    welcome: 'Welcome to XAVIOR — your legal workspace.',
    lookAround: 'Look around, or ask me anything.',
  },
  founder: {
    id: 'founder',
    welcome: 'Welcome to XAVIOR.',
    lookAround: 'Look around, or ask me anything.',
  },
}

/**
 * Email-domain → recipe id. Extensible: add a domain here to route signups from
 * that domain into a tailored recipe.
 */
export const EMAIL_DOMAIN_RECIPES: Record<string, string> = {
  'nulegal.com': 'lawyer',
}

/**
 * sessionStorage key the captured `?ob` param is persisted under. The `xavior:`
 * prefix is mandatory (apps/web AGENTS.md storage-namespacing rule).
 */
const OB_PARAM_STORAGE_KEY = 'xavior:onboarding-ob'

/**
 * Read the `?ob` URL param directly from `window.location.search`. Guarded for
 * non-browser (SSR/test) contexts. Returns the raw value or null when absent.
 */
function readObFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const value = new URLSearchParams(window.location.search).get('ob')
    return value && value.length > 0 ? value : null
  } catch {
    return null
  }
}

/**
 * Capture the onboarding `?ob` URL param as EARLY as possible and persist it in
 * sessionStorage. Called at MODULE-EVAL (the bottom of this file, before any
 * component mounts) AND from a React effect at app entry (AppContent). The
 * module-eval call is what wins the race: ChatBox's `onboardingRecipe` useMemo
 * reads the persisted value on its FIRST render, and on the merged redirect flow
 * ChatBox can mount before the App effect has flushed — a mount-time read with no
 * prior capture would memoize `null` and resolve the default recipe. The App
 * effect remains as a belt-and-suspenders re-capture within the React lifecycle
 * (and so the value is captured even if this module was somehow first evaluated
 * after the relevant render). The captured value is CLEARED once onboarding ends
 * (see clearOnboardingObParam).
 *
 * Persistence is for reload-survival of recipe resolution ONLY (so the welcome
 * copy still resolves after a hard reload mid-onboarding); it is cleared once
 * onboarding ends — see clearOnboardingObParam.
 *
 * No-op when the param is absent: we must NOT overwrite a previously-stored value
 * with null (a later render/mount that no longer carries `?ob` in the URL would
 * otherwise wipe the captured intent).
 */
export function captureOnboardingObParam(): void {
  if (typeof window === 'undefined') return
  const value = readObFromUrl()
  if (!value) return
  try {
    window.sessionStorage.setItem(OB_PARAM_STORAGE_KEY, value)
  } catch {
    // sessionStorage unavailable (quota / private mode) — fall back to the live
    // URL read in readOnboardingObParam.
  }
}

/**
 * Clear the persisted `?ob` recipe param. Called once onboarding ends
 * (ChatBox's `onboardingActive → false` effect) so the captured recipe intent is
 * not an indefinitely-lingering sessionStorage key. After onboarding the recipe
 * source is no longer needed, so dropping it is safe. Idempotent.
 */
export function clearOnboardingObParam(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(OB_PARAM_STORAGE_KEY)
  } catch {
    // sessionStorage unavailable — nothing persisted to clear.
  }
}

/**
 * Read the captured onboarding `?ob` param. Prefers the persisted value (captured
 * at module-eval, immune to mount timing); falls back to the live URL read so a
 * direct `?ob=…` visit still works even if capture never ran (e.g. SSR/test).
 */
export function readOnboardingObParam(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.sessionStorage.getItem(OB_PARAM_STORAGE_KEY)
    if (stored && stored.length > 0) return stored
  } catch {
    // Ignore storage access failures and fall through to the live URL read.
  }
  return readObFromUrl()
}

/**
 * Resolve which recipe to run. Priority:
 *   1. explicit `?ob=<id>` URL param (if it names a known recipe)
 *   2. email domain match (EMAIL_DOMAIN_RECIPES)
 *   3. default
 *
 * Always returns a valid recipe — an unknown `?ob` value falls through to the
 * domain/default resolution rather than throwing.
 */
export function resolveOnboardingRecipe(opts: {
  obParam?: string | null
  email?: string | null
}): OnboardingRecipe {
  const { obParam, email } = opts

  // 1) Explicit URL param wins when it names a known recipe.
  if (obParam) {
    const byParam = ONBOARDING_RECIPES[obParam]
    if (byParam) return byParam
  }

  // 2) Email-domain match.
  if (email) {
    const at = email.lastIndexOf('@')
    if (at >= 0) {
      const domain = email.slice(at + 1).toLowerCase()
      const recipeId = EMAIL_DOMAIN_RECIPES[domain]
      if (recipeId && ONBOARDING_RECIPES[recipeId]) {
        return ONBOARDING_RECIPES[recipeId]
      }
    }
  }

  // 3) Default fallback.
  return ONBOARDING_RECIPES.default
}

// Capture `?ob` at module-eval — the earliest possible point, before any
// component mounts — so ChatBox's recipe useMemo can't lose the race against the
// App-entry effect capture (#4598 follow-up). No-op outside the browser and when
// `?ob` is absent (never overwrites a previously-stored value with null).
captureOnboardingObParam()
